import type { ComponentType, SVGProps } from 'react';
import * as flags from 'country-flag-icons/react/3x2';

/**
 * Renders a country as a flag + English name (e.g. 🇬🇧 United Kingdom)
 * from an ISO 3166-1 alpha-2 code. Falls back to the raw code for
 * unknown codes and an em-dash when absent.
 *
 * Important: the name is resolved via `Intl.DisplayNames`, not the
 * sign-up allowlist in `lib/auth/countries.ts`. That allowlist is the
 * MVP-scoped set of countries a *merchant* can self-register from (UK +
 * USA + EU 27); a merchant's *orders* can ship anywhere in the world,
 * so the destination column must support every ISO code. The flag SVG
 * likewise comes straight from `country-flag-icons`, which carries the
 * full ISO 3166-1 set.
 *
 * The flag is decorative (`aria-hidden`) — the adjacent visible name is
 * the accessible label, so titling the flag would make screen readers
 * announce the country twice.
 */
type FlagComponent = ComponentType<SVGProps<SVGSVGElement> & { title?: string }>;

const REGION_NAMES = new Intl.DisplayNames(['en'], { type: 'region' });

function flagComponentFor(code: string): FlagComponent | undefined {
  return (flags as Record<string, FlagComponent | undefined>)[code];
}

interface CountryLabelProps {
  readonly code?: string;
}

export function CountryLabel({ code }: CountryLabelProps) {
  if (!code) return <>—</>;
  const name = REGION_NAMES.of(code);
  // `Intl.DisplayNames#of` returns the input code verbatim when it
  // doesn't recognise it — surface that as the bare-code fallback so
  // garbage data is visible rather than silently translated.
  if (!name || name === code) return <>{code}</>;
  const Flag = flagComponentFor(code);
  return (
    <span className="flex items-center gap-2">
      {Flag ? (
        <Flag aria-hidden="true" style={{ width: 21, height: 14 }} className="rounded-xs" />
      ) : null}
      <span>{name}</span>
    </span>
  );
}
